import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
} from "date-fns";
import "../assets/css/columnFilter.css";

/**
 * Reusable date-range column filter (DevExpress-style).
 * Presets on the left, a range calendar on the right, Clear / Apply at the bottom.
 *
 * Props:
 *   value    = { start, end }  (Date | string | null)
 *   onChange = (start, end) => void   // Date objects or null
 *   placeholder?: string
 *
 * Reusable anywhere — it portals its dropdown to <body> so it never gets
 * clipped by a scrolling table/container.
 */
const buildPresets = () => {
  const now = new Date();
  return [
    { label: "Today", range: [now, now] },
    { label: "Yesterday", range: [subDays(now, 1), subDays(now, 1)] },
    {
      label: "This Week",
      range: [
        startOfWeek(now, { weekStartsOn: 1 }),
        endOfWeek(now, { weekStartsOn: 1 }),
      ],
    },
    { label: "This Month", range: [startOfMonth(now), endOfMonth(now)] },
    {
      label: "Last Month",
      range: [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
    },
    { label: "Last 3 Months", range: [subMonths(now, 3), now] },
    { label: "This Year", range: [startOfYear(now), endOfYear(now)] },
  ];
};

const ColumnDateFilter = ({ value, onChange, placeholder = "Select date..." }) => {
  const start = value?.start ? new Date(value.start) : null;
  const end = value?.end ? new Date(value.end) : null;

  const [open, setOpen] = useState(false);
  const [temp, setTemp] = useState([start, end]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  // Sync the working range when the committed value changes.
  useEffect(() => {
    setTemp([
      value?.start ? new Date(value.start) : null,
      value?.end ? new Date(value.end) : null,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.start, value?.end]);

  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      const width = 520;
      let left = r.left + window.scrollX;
      if (left + width > window.innerWidth) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setPos({ top: r.bottom + window.scrollY + 6, left });
    }
    setTemp([start, end]);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDocDown);
    return () => window.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const handleApply = () => {
    onChange(temp[0] || null, temp[1] || null);
    setOpen(false);
  };
  const handleClear = () => {
    setTemp([null, null]);
    onChange(null, null);
    setOpen(false);
  };

  const triggerText =
    start && end
      ? `${format(start, "dd MMM yyyy")} – ${format(end, "dd MMM yyyy")}`
      : start
      ? `${format(start, "dd MMM yyyy")} – …`
      : placeholder;

  const rangeLabel = temp[0]
    ? `${format(temp[0], "dd/MM/yyyy")} → ${
        temp[1] ? format(temp[1], "dd/MM/yyyy") : "…"
      }`
    : "No range selected";

  return (
    <div className="cdf-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={`cdf-trigger ${start ? "has-value" : ""}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <i className="bi bi-calendar3"></i>
        <span className="cdf-trigger-text">{triggerText}</span>
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={popRef}
            className="cdf-pop"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="cdf-presets">
              {buildPresets().map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className="cdf-preset"
                  onClick={() => setTemp(p.range)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="cdf-cal">
              <DatePicker
                selected={temp[0]}
                startDate={temp[0]}
                endDate={temp[1]}
                onChange={(dates) => setTemp(dates)}
                selectsRange
                inline
                monthsShown={1}
                calendarStartDay={1}
              />

              <div className="cdf-footer">
                <span className="cdf-range">{rangeLabel}</span>
                <div className="cdf-actions">
                  <button
                    type="button"
                    className="cdf-btn cdf-clear"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="cdf-btn cdf-apply"
                    onClick={handleApply}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ColumnDateFilter;
